import { types, getRoot, flow, Instance, cast } from 'mobx-state-tree';
import QueryString from 'qs';
import * as R from 'ramda';
import { ApiStore } from '@src/features/common';
import * as FreelancersAPI from '@src/services/freelancers';
import { mapStringHelper } from '@src/helpers/mapStringHelper';
import { IResourcesStore, IRouterStore } from '@src/models';
import { toast } from 'src/components';

// ToDo add pagination
// Create abstraction so u can use different filters
// write tests

export const Client = types.model('Client', {
  id: types.number,
  jobs_count: types.number,
  logo_url: types.maybeNull(types.string),
  name: types.string,
  owned_by: types.model({
    id: types.number,
    first_name: types.string,
    last_name: types.string,
    email: types.string
  }),
  website: types.maybeNull(types.string)
});

const Filter = types.model({
  label: types.union(types.string, types.number),
  value: types.union(types.string, types.number)
});
type IFilter = Instance<typeof Filter>;

const Filters = types
  .model('Client', {
    status: types.array(Filter),
    industry: types.array(Filter),
    owned_by: types.array(Filter)
  })
  .actions(self => {
    return {
      getFilterValue: (label: string): Array<IFilter | null> | IFilter => {
        const {
          location: { search }
        } = getRoot<{ routerStore: IRouterStore; resourcesStore: IResourcesStore }>(self).routerStore;

        const searchParams = QueryString.parse(search, { ignoreQueryPrefix: true });

        if (Array.isArray(searchParams[label]) || !Boolean(searchParams[label])) {
          const filters =
            searchParams[label]?.map(param => self[label].find(({ value }) => String(value) === param)) || [];

          return filters.filter(Boolean);
        } else {
          return { label, value: searchParams[label] };
        }
      },
      initFilters: filters => {
        self.status = filters.status.map(mapStringHelper);
        self.industry = filters.industry.map(mapStringHelper);
        self.owned_by = filters.owned_by.map(({ id, first_name, last_name }) => ({
          label: first_name + ' ' + last_name,
          value: id
        }));
      },
      setFilter: (filters: Array<IFilter> | IFilter | null, name) => {
        if (filters === null) return;
        const {
          location: { search },
          push
        } = getRoot<{ routerStore: IRouterStore; resourcesStore: IResourcesStore }>(self).routerStore;

        const returnFilterValue = Array.isArray(filters) ? filters.map(({ value }) => value) : filters.value;

        // get search params

        const searchParams = QueryString.parse(search, { ignoreQueryPrefix: true });

        // push to that searchParam
        const newSearch = QueryString.stringify(
          {
            page: 1,
            per_page: Number.MAX_SAFE_INTEGER,
            ...R.mergeDeepLeft({ [name]: returnFilterValue }, searchParams)
          },
          { addQueryPrefix: true, arrayFormat: 'brackets' }
        );
        push({ search: newSearch });
      }
    };
  });

export const ClientsStore = types
  .compose(
    ApiStore,
    types.model({
      filters: types.optional(Filters, {
        status: [],
        industry: [],
        owned_by: []
      })
    }),
    types.model({
      data: types.array(Client)
    })
  )
  .actions(self => {
    return {
      fetchData: flow(function*() {
        const rootStore = getRoot<{ routerStore: IRouterStore; resourcesStore: IResourcesStore }>(self);
        const {
          location: { search }
        } = rootStore.routerStore;
        try {
          // first here I need to check does search query have something?
          // return entities, filters and pagination
          const res = yield self.fetch(FreelancersAPI.getClients + (search === '' ? '?page=1&per_page=40000' : search));
          self.data = res.entities;
          self.filters.initFilters(res.filters);
        } catch (error) {}

        // fetchResources for Modal
        const resourcesStore = rootStore.resourcesStore;

        yield resourcesStore.getCountries();
        yield resourcesStore.getIndustries();
      }),
      addClient: flow(function*(values) {
        const res = yield self.post(FreelancersAPI.addClient, values);
        self.data = cast([res, ...self.data]);
        toast.info('Success');
      })
    };
  });
